/// <amd-module name="Wit/Settings/BoardsExternalIntegration/components/RepositoryPivot" />
import * as React from "react";
import { ITfsPageService } from "Tfs/Platform/Page";
import { equals, format } from "VSS/Core/Util/String";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssHistoryService } from "VSS/Platform/History";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IButtonProps } from "VSSUI/Button";
import { Link } from "VSSUI/Link";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { IPivotBarAction } from "VSSUI/PivotBar";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { ExternalGitRepo } from "Wit/Clients/Internal/Contracts/ExternalEvents";
import { ExternalConnection, GitHubUnifiedInstallationResponse, ServiceEndpoint } from "Wit/Clients/Internal/Contracts/WorkItemTracking";
import { ConnectionsList, showConfirmationDialog } from "Wit/Settings/BoardsExternalIntegration/components/common";
import { isHosted } from "Wit/Settings/BoardsExternalIntegration/components/common/Utilities";
import { Panel } from "Wit/Settings/BoardsExternalIntegration/components/panels/Panel.enum";
import { showPanel } from "Wit/Settings/BoardsExternalIntegration/components/panels/PanelManager";
import { ZeroData } from "Wit/Settings/BoardsExternalIntegration/components/ZeroData";
import { DocUrls, GitHubConnectionConstants } from "Wit/Settings/BoardsExternalIntegration/constants/Constants";
import { GitHubOAuthState, IPivotProps } from "Wit/Settings/BoardsExternalIntegration/contracts/Models";
import * as Resources from "Wit/Settings/BoardsExternalIntegration/Resources";
import { GitHubIntegrationCIActions, publishGitHubIntegrationTelemetry } from "Wit/Settings/BoardsExternalIntegration/telemetry/GitHub";
import {
    createAuthRequest,
    fetchConnectionsData,
    findExistingConnectionByGitHubHandle,
    findExistingConnectionById,
    getErrorMessageFromCode,
    getFetchGitHubConnectionsErrorText,
    getFriendlyConnectionName,
    getProviderKey,
    removeConnection,
    removeRedirectParamsFromHistoryState
} from "Wit/Settings/BoardsExternalIntegration/utils";

export interface IRepositoryPivotState {
    connections: ExternalConnection[];
    loading: boolean;
    hasWritePermission: boolean;
    gitHubOAuthState: GitHubOAuthState;
    errorMessage?: string | JSX.Element;
    actionableActiveAppConnection?: ExternalConnection;
    actionableInActiveAppConnection?: ExternalConnection;
    spinnerLabelText?: string;
    serviceEndpoint?: ServiceEndpoint;
}

export class RepositoryPivot extends VssComponent<IPivotProps, IRepositoryPivotState> {
    private static readonly gitHubEnterpriseOAuthDataProviderId = "ms.vss-work-web.github-enterprise-oauth-complete-data-provider";
    private static readonly gitHubOAuthDataProviderId = "ms.vss-work-web.github-oauth-complete-data-provider";
    private readonly _contributionService: IVssContributionService;
    private readonly _historyService: IVssHistoryService;
    private readonly _projectId: string;
    private _adminOpenCIWritten: boolean;

    constructor(props: IPivotProps, context: IVssComponentContext) {
        super(props, context);

        const pageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const pageData = pageService.getData();
        // tslint:disable-next-line:no-non-null-assertion
        this._projectId = pageData!.project!.id;

        this._contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        this._historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");

        this.state = this._getOAuthState();
    }

    public componentDidMount() {
        this._fetchConnectionData();
    }

    public async componentDidUpdate(prevProps: IPivotProps, prevState: IRepositoryPivotState): Promise<void> {
        if (!this._adminOpenCIWritten) {
            // write once and only once open page event if it's admin
            this._adminOpenCIWritten = true;
            publishGitHubIntegrationTelemetry(
                this.context,
                this.state.hasWritePermission ? GitHubIntegrationCIActions.ADMIN_OPEN : GitHubIntegrationCIActions.ADMIN_OPEN_NONPROJECTADMIN
            );
        }
        // Show 'New connection' button only when there is at least one connection available and the current user has admin permissions
        // We verify the state of the connections and the permissions on every update
        if (
            (!prevState.connections && this.state.connections) ||
            (prevState.connections && !this.state.connections) ||
            (prevState.connections && this.state.connections && prevState.connections.length !== this.state.connections.length) ||
            prevState.hasWritePermission !== this.state.hasWritePermission ||
            prevState.loading !== this.state.loading
        ) {
            if (this.state.connections && this.state.connections.length > 0 && this.state.hasWritePermission) {
                this.props.updateHubCommands(this._getHubCommands(this.state.loading));
            } else {
                this.props.updateHubCommands([]);
            }
        }

        if (prevState.hasWritePermission !== this.state.hasWritePermission) {
            const { connections, gitHubOAuthState, serviceEndpoint } = this.state;
            // If we have 'key' then it means it was redirected from OAuth - show add repo panel
            // If we have key equals useWellKnownStrongBoxLocation then we are from GitHub app installation redirection - show add repo panel
            // 'configurationid' and 'enterpriseurl' is used for GHE only
            const {
                key,
                code /* code from install oauth flow  */,
                existingConnectionId,
                errorCode,
                isGHE, // This is for error case only to give more precise error message
                acquisitionId
            } = gitHubOAuthState;

            const isGitHubApp = key === GitHubConnectionConstants.UseWellKnownStrongBoxLocationKey;

            try {
                if (errorCode) {
                    // If we had an error during OAuth, show the error message
                    this.setState({
                        errorMessage: (
                            <span>
                                {getErrorMessageFromCode(errorCode, isGHE)}&nbsp;&nbsp;
                                <Link href={DocUrls.GitHubAndAzureBoards_Portal} target="_blank">
                                    {Resources.LearnMore}
                                </Link>
                            </span>
                        ),
                        loading: false,
                        spinnerLabelText: undefined
                    });
                    return;
                }

                // Unified experience is shown when the ff is on and
                // 1. Key = useWellKnownStrongBoxLocation // Happens on initial OAuth callback
                // 2. code is not null // Happens when GitHub redirects with user's OAuth code after installation of GitHub app
                // The if part should not get executed when install from GitHub or update install from GitHub
                if (
                    (equals(key, GitHubConnectionConstants.UseWellKnownStrongBoxLocationKey, true) || code) &&
                    !acquisitionId // For fresh install, we get acquisition id. So we need this to differentiate between the fresh install and when you are adding repo from GH and not using unified experience.
                ) {
                    // This means we're here after the authorization redirect so, stop the spinner as we completed the
                    // busy operations above and we're about to show the Org Selection panel or 'Add repo' panel
                    this._showUnifiedExperiencePanel(key, existingConnectionId);
                } else if (isGitHubApp || serviceEndpoint) {
                    // If it is a Githubapp, find connection using connectionId and display the panel for add repo.
                    const existingConnection = isGitHubApp
                        ? findExistingConnectionById(connections, existingConnectionId)
                        : findExistingConnectionByGitHubHandle(connections, serviceEndpoint!.gitHubHandle, serviceEndpoint!.url);
                    if (existingConnection) {
                        if (isGitHubApp) {
                            this.setState({
                                loading: false,
                                actionableActiveAppConnection: existingConnection
                            });
                        } else {
                            this.setState({
                                loading: false,
                                errorMessage: format(Resources.DuplicateConnectionError, serviceEndpoint!.gitHubHandle),
                                actionableActiveAppConnection: undefined
                            });
                        }
                    } else {
                        // This means we're here after the authorization redirect so, stop the spinner as we completed the
                        // busy operations above and we're about to show the 'Add repo' panel
                        this.setState({ loading: false }, () => {
                            // Show connection panel
                            const providerKey = getProviderKey(serviceEndpoint!.url);
                            const connection: ExternalConnection = {
                                providerKey: providerKey,
                                serviceEndpoint,
                                externalGitRepos: [] as ExternalGitRepo[]
                            } as ExternalConnection;
                            this._showPanel(Panel.AddRepo, connection, undefined /* onPanelClosed */, false /* performOAuthCheck */, isGitHubApp);
                        });
                    }
                } else {
                    this.setState({ loading: false });
                }
            } catch (error) {
                this.setState({
                    loading: false,
                    spinnerLabelText: undefined,
                    errorMessage: error.message
                });
            } finally {
                removeRedirectParamsFromHistoryState(this.context.pageContext);
            }
        }
    }

    public render(): JSX.Element {
        if (this.state.loading) {
            const labelText = this.state.spinnerLabelText ? this.state.spinnerLabelText : Resources.SpinnerFetchingConnectionsText;
            return (
                <>
                    {this._renderErrorMessage()}
                    <div className="flex flex-grow justify-center flex-center">
                        <Spinner size={SpinnerSize.large} label={labelText} />
                    </div>
                </>
            );
        } else if (this.state.connections && this.state.connections.length > 0) {
            return (
                <div className="repository-pivot-container h-scroll-auto">
                    {this.state.actionableActiveAppConnection && this._renderActiveConnectionInfoMessage()}
                    {this.state.actionableInActiveAppConnection && this._renderInActiveConnectionInfoMessage()}
                    {this._renderErrorMessage()}
                    <ConnectionsList
                        connections={this.state.connections}
                        showPanel={this._showPanel}
                        onOpenRemoveConnectionDialog={this._onOpenRemoveConnectionDialogFromConnectionsList}
                        hasWritePermission={this.state.hasWritePermission}
                    />
                </div>
            );
        } else {
            return (
                <>
                    {this._renderErrorMessage()}
                    <ZeroData
                        hasWritePermission={this.state.hasWritePermission}
                        showPanel={this._showPanel}
                        onConnectButtonClick={this._onZeroDataConnectButtonClick}
                        onOAuthError={this._onOAuthError}
                    />
                </>
            );
        }
    }

    private readonly _onOAuthError = (error: Error) => {
        this.setState({
            loading: false, // important to set this otherwise, we'll be stuck at loading when there is an error while authorizing
            errorMessage: error.message
        });
    };

    private readonly _onZeroDataConnectButtonClick = () => {
        this.setState({
            loading: true,
            spinnerLabelText: Resources.SpinnerConnectingAccountText
        });
    };

    private readonly _renderErrorMessage = (): JSX.Element | undefined => {
        if (this.state.errorMessage) {
            return (
                <div className="repository-pivot connections-list-err-card">
                    <MessageBar severity={MessageBarSeverity.Error} onDismiss={this._onDismissErrorMessage}>
                        {this.state.errorMessage}
                    </MessageBar>
                </div>
            );
        } else {
            return undefined;
        }
    };

    private readonly _onDismissErrorMessage = () => {
        this.setState({
            errorMessage: undefined
        });
    };

    private readonly _renderActiveConnectionInfoMessage = (): JSX.Element | undefined => {
        // Where there is a duplicated connection in GitHub app scenario, show a info message to the user that is actionable.
        const duplicatedConnection = this.state.actionableActiveAppConnection;
        if (duplicatedConnection) {
            const buttonProps: IButtonProps[] = [
                {
                    text: Resources.AddRepos,
                    onClick: () => {
                        this.setState(
                            {
                                actionableActiveAppConnection: undefined
                            },
                            () => {
                                this._showPanel(Panel.AddRepo, duplicatedConnection, undefined, /* performOAuthCheck*/ false, /*isGitHubApp*/ true);
                                publishGitHubIntegrationTelemetry(this.context, GitHubIntegrationCIActions.ADMIN_ADD_EXISTING_REPOS);
                            }
                        );
                    }
                },
                {
                    text: Resources.RemoveRepos,
                    onClick: () => {
                        this.setState(
                            {
                                actionableActiveAppConnection: undefined
                            },
                            () => {
                                this._showPanel(Panel.RemoveRepo, duplicatedConnection);
                                publishGitHubIntegrationTelemetry(this.context, GitHubIntegrationCIActions.ADMIN_REMOVE_EXISTING_REPOS);
                            }
                        );
                    }
                }
            ];

            return (
                <div className="repository-pivot connections-list-info-card">
                    <MessageBar severity={MessageBarSeverity.Info} buttonProps={buttonProps}>
                        {format(Resources.ExistingConnectionInfo, getFriendlyConnectionName(duplicatedConnection))}
                    </MessageBar>
                </div>
            );
        } else {
            return undefined;
        }
    };

    private readonly _renderInActiveConnectionInfoMessage = (): JSX.Element | undefined => {
        // Where there is a in active connection in GitHub app scenario, show a info message to the user that is actionable.
        const inActiveAppConnection = this.state.actionableInActiveAppConnection;
        if (inActiveAppConnection) {
            const buttonProps: IButtonProps[] = [
                {
                    text: Resources.RemoveConnection,
                    onClick: () => {
                        this.setState(
                            {
                                actionableInActiveAppConnection: undefined
                            },
                            () => {
                                this._onOpenRemoveConnectionDialogFromConnectionsList(inActiveAppConnection);
                                publishGitHubIntegrationTelemetry(this.context, GitHubIntegrationCIActions.ADMIN_REMOVE_INACTIVE_CONNECTION);
                            }
                        );
                    }
                }
            ];

            return (
                <div className="repository-pivot connections-list-inactive-info-card">
                    <MessageBar severity={MessageBarSeverity.Info} buttonProps={buttonProps}>
                        {format(Resources.InActiveConnectionInfo, getFriendlyConnectionName(inActiveAppConnection))}
                    </MessageBar>
                </div>
            );
        } else {
            return undefined;
        }
    };

    private async _fetchConnectionData(): Promise<void> {
        try {
            const connectionData = await fetchConnectionsData(this.context.pageContext, true /* includeInvalidConnections */);
            if (connectionData) {
                const { hasWritePermission } = connectionData;
                const connections = connectionData.externalConnections || [];
                this.setState({
                    connections,
                    spinnerLabelText: undefined,
                    hasWritePermission: hasWritePermission,
                    loading: hasWritePermission, // Stop the spinner right away for readonly users as there's nothing more to do in ComponentDidUpdate
                    actionableInActiveAppConnection: this._getInActiveConnection(connections)
                });
            } else {
                this.setState({
                    errorMessage: getFetchGitHubConnectionsErrorText(),
                    loading: false
                });
            }
        } catch (error) {
            this.setState({
                errorMessage: error.message,
                loading: false
            });
        }
    }

    private _showUnifiedExperiencePanel(key: string, existingConnectionId: string) {
        const response = this._contributionService.getData<GitHubUnifiedInstallationResponse>(
            GitHubConnectionConstants.GitHubPostInstallDataProviderId
        );
        const providerKey = GitHubConnectionConstants.GitHubProviderKey;
        let connection: ExternalConnection = {
            providerKey: providerKey,
            externalGitRepos: [] as ExternalGitRepo[]
        } as ExternalConnection;

        if (response) {
            // This case happens when users have changed the repository selection at GitHub, so we need to re-verify before creating connection
            if (response.isReposAltered) {
                //in edit an app connection there will be existingConnectionId in the response, we will use that to accomplish update
                connection = response.existingConnectionId
                    ? findExistingConnectionById(this.state.connections, response.existingConnectionId)!
                    : connection;

                this.setState({ loading: false }, () => {
                    this._showPanel(Panel.AddRepo, connection, undefined /* onPanelClosed */, false /* performOAuthCheck */, true /* isGitHubApp */);
                });

                publishGitHubIntegrationTelemetry(this.context, GitHubIntegrationCIActions.UNIFIED_EXPERIENCE_ADD_REPO_AFTER_INSTALLATION, {
                    addedNumberOfRepos: connection.externalGitRepos.length,
                    unifiedExperience: true
                });
            } else if (response.externalConnectionProvisionResult) {
                // Here connection is created successfully after installation
                const savedConnection = response.externalConnectionProvisionResult.provisionedConnection;
                this.setState({
                    loading: false,
                    errorMessage: undefined
                });

                publishGitHubIntegrationTelemetry(this.context, GitHubIntegrationCIActions.ADMIN_ADD_REPOS, {
                    addedNumberOfRepos: savedConnection.externalGitRepos.length,
                    totalNumberOfRepos: savedConnection.externalGitRepos.length,
                    manually: false,
                    providerKey: savedConnection.providerKey,
                    authType: savedConnection.serviceEndpoint.authorizationScheme,
                    succeeded: true,
                    errorMessage: undefined,
                    unifiedExperience: true
                });
            } else {
                this.setState({
                    loading: false,
                    errorMessage: response.errorMessage
                });
            }
        } else {
            this.setState({ loading: false }, () => {
                connection = existingConnectionId ? findExistingConnectionById(this.state.connections, existingConnectionId)! : connection;
                showPanel(
                    this.context.pageContext,
                    Panel.OrgSelection,
                    this.state.connections,
                    connection,
                    true /* isGitHubApp */,
                    undefined /* onPanelClosed */,
                    (connection: ExternalConnection, isDeleted: boolean, errorMsg: any) =>
                        this.setStateWithUpdatedConnection(connection, isDeleted, errorMsg),
                    () =>
                        this.setState({
                            gitHubOAuthState: {}
                        }),
                    false /* isAcquisitionFlow */,
                    true /* showUnifiedExperience */
                );
            });
        }
    }

    private readonly _onNewConnection = () => {
        this._showPanel(Panel.NewConnection);
    };

    private readonly _showPanel = (
        panel: Panel,
        connection?: ExternalConnection,
        onPanelClosed?: () => void,
        performOAuthCheck?: boolean,
        isGitHubApp?: boolean
    ): void => {
        // If it is an existing github app connection with perform oAuth check, then perform a Oauth before showing the repo picker
        if (connection && connection.id && isGitHubApp && performOAuthCheck) {
            this._performOAuth(connection.id);
        } else {
            showPanel(
                this.context.pageContext,
                panel,
                this.state.connections,
                connection,
                isGitHubApp,
                onPanelClosed,
                (connection: ExternalConnection, isDelete: boolean, partialProvisionError: string) =>
                    this.setStateWithUpdatedConnection(connection, isDelete, partialProvisionError),
                () =>
                    this.setState({
                        gitHubOAuthState: {}
                    })
            );
        }
    };

    private _getOAuthState(): IRepositoryPivotState {
        const pivotState: IRepositoryPivotState = {
            connections: [],
            loading: true,
            hasWritePermission: false,
            gitHubOAuthState: {}
        };

        const gheServiceEndpoint = this._contributionService.getData<ServiceEndpoint>(RepositoryPivot.gitHubEnterpriseOAuthDataProviderId);
        const ghServiceEndpoint =
            isHosted(this.context.pageContext) && this._contributionService.getData<ServiceEndpoint>(RepositoryPivot.gitHubOAuthDataProviderId);

        if (gheServiceEndpoint) {
            return {
                ...pivotState,
                serviceEndpoint: gheServiceEndpoint
            };
        } else if (ghServiceEndpoint) {
            return {
                ...pivotState,
                serviceEndpoint: ghServiceEndpoint
            };
        } else {
            // Read OAuth state from query params
            const historyState = this._historyService.getState();
            if (historyState) {
                return {
                    ...pivotState,
                    gitHubOAuthState: historyState
                };
            }
        }

        // Default state if nothing returned
        return pivotState;
    }

    private readonly _onOpenRemoveConnectionDialogFromConnectionsList = (connection: ExternalConnection, onDialogClose?: () => void): void => {
        showConfirmationDialog(
            this.context,
            format(Resources.ConfirmationMessageConnection, getFriendlyConnectionName(connection)),
            Resources.ConfirmationRemove,
            Resources.ConfirmationTitle,
            async (): Promise<void> => {
                this.setState({
                    loading: true,
                    spinnerLabelText: Resources.SpinnerRemovingConnectionText
                });

                try {
                    await removeConnection(this.context.pageContext, connection);
                    this.setStateWithUpdatedConnection(connection, true, undefined);
                } catch (error) {
                    this.setStateWithUpdatedConnection(connection, false, error.messsage);
                }

                onDialogClose && onDialogClose();
            },
            onDialogClose
        );
    };

    private setStateWithUpdatedConnection(updatedConnection: ExternalConnection, isDelete?: boolean, errorMsg?: string) {
        const connections = this.state.connections.filter(c => !equals(c.serviceEndpoint.id, updatedConnection.serviceEndpoint.id, true));
        if (!isDelete) {
            connections.push(updatedConnection);
        }

        this.setState({
            connections: connections,
            errorMessage: errorMsg,
            gitHubOAuthState: {}, // reset the OAuth, so that we can authorize again
            loading: false,
            spinnerLabelText: undefined,
            actionableActiveAppConnection: undefined,
            actionableInActiveAppConnection: this._getInActiveConnection(connections)
        });
    }

    private _getInActiveConnection(connections: ExternalConnection[]): ExternalConnection | undefined {
        // Get uninstalled github app connections. We pick the first one if there are more than one for the message bar.
        const uninstalledGitHubAppConnections = connections.filter(
            c =>
                equals(c.serviceEndpoint.authorizationScheme, GitHubConnectionConstants.GitHubAppScheme, true /* ignorecase */) &&
                (!!c.statusMessage || !c.serviceEndpoint.isEndpointValid)
        );

        let uninstalledGitHubAppConnection: ExternalConnection | undefined;
        if (uninstalledGitHubAppConnections.length > 0) {
            uninstalledGitHubAppConnection = uninstalledGitHubAppConnections[0];
        }

        return uninstalledGitHubAppConnection;
    }

    private async _performOAuth(connectionId: string): Promise<void> {
        const onError = (error: Error) => {
            this.setState({
                errorMessage: error.message || Resources.GitHubOAuthAppValidationError
            });
        };

        try {
            createAuthRequest(this.context.pageContext, onError, connectionId);
        } catch (error) {
            onError(error);
        }
    }

    private readonly _getHubCommands = (disabled: boolean): IPivotBarAction[] => {
        return [
            {
                key: "New Connection",
                name: Resources.NewConnection,
                important: true,
                iconProps: { iconName: "Add" },
                onClick: this._onNewConnection,
                disabled
            },
            {
                key: "Help",
                name: Resources.Help,
                important: true,
                iconProps: { iconName: "Unknown" },
                href: DocUrls.GitHubAndAzureBoards_Portal,
                target: "_blank"
            }
        ];
    };
}

VssComponent.register("RepositoryPivot", RepositoryPivot);
